import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Customer, OfferGroupService } from '../../../../services/offer-group.service';

export interface QuotationOptions {
  /** Customer name - optional, defaults to 'Walk-In Customer' */
  customerName: string;
  /** 10-digit mobile number - optional */
  mobileNumber: string;
  /** Customer Address - optional */
  customerAddress?: string;
  /** Customer GSTIN - optional */
  customerGSTIN?: string;
  /** Quotation validity date */
  validUntil: Date;
  /** Whether to include GST breakdown in the PDF */
  withGst: boolean;
}

@Component({
  selector: 'app-quotation-options-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    CalendarModule,
    AutoCompleteModule,
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      (visibleChange)="onVisibleChange($event)"
      [modal]="true"
      [closable]="true"
      [closeOnEscape]="true"
      [draggable]="false"
      [resizable]="false"
      [showHeader]="false"
      styleClass="pos-dlg pos-dlg-md premium-dialog"
      (onShow)="customerNameAc.inputEL?.nativeElement?.focus()"
      (onHide)="onCancel()"
    >
        <div class="premium-header">
          <div class="premium-header-content">
            <h3 class="premium-title">Quotation Options</h3>
            <p class="premium-subtitle">Customer, validity and tax details for the PDF</p>
          </div>
          <button
            class="premium-close-btn"
            (click)="onCancel()"
            type="button"
            aria-label="Close quotation options"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="premium-body">

          <!-- Customer Name -->
          <div class="form-group mb-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="field-label" for="qo-cust-name">Customer Name</label>
              <span class="optional-badge">Optional</span>
            </div>
            <div class="input-with-icon-wrapper">
              <i class="pi pi-user input-lead-icon"></i>
              <p-autoComplete
                #customerNameAc
                [(ngModel)]="form.customerName"
                (ngModelChange)="onCustomerNameModelChange($event)"
                [suggestions]="filteredCustomers"
                (completeMethod)="filterCustomers($event)"
                (onSelect)="onCustomerSelect($event)"
                field="customer_name"
                [dropdown]="false"
                [forceSelection]="false"
                [minLength]="0"
                [showEmptyMessage]="true"
                emptyMessage="No customer found (will save as new)"
                placeholder="Search existing customer or enter name"
                appendTo="body"
                styleClass="w-100"
                inputStyleClass="w-100 custom-form-input ps-4"
              >
                <ng-template let-customer pTemplate="item">
                  <div class="d-flex justify-content-between align-items-center py-1">
                    <div>
                      <div class="fw-bold text-dark" style="font-size: var(--fs-md);">
                        {{ customer.customer_name }}
                      </div>
                      <small class="text-muted" style="font-size: var(--fs-sm);" *ngIf="customer.city || customer.state">
                        {{ customer.city || '' }} {{ customer.state ? ', ' + customer.state : '' }}
                      </small>
                    </div>
                    <span class="badge bg-light text-primary border ms-2" style="font-size: var(--fs-sm);">
                      {{ customer.mobile_no }}
                    </span>
                  </div>
                </ng-template>
              </p-autoComplete>
            </div>
          </div>

          <!-- Mobile Number -->
          <div class="form-group mb-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="field-label" for="qo-mobile">Mobile Number</label>
              <span class="optional-badge">Optional</span>
            </div>
            <div class="input-with-icon-wrapper">
              <i class="pi pi-phone input-lead-icon"></i>
              <p-autoComplete
                [(ngModel)]="form.mobileNumber"
                (ngModelChange)="onMobileNumberModelChange($event)"
                [suggestions]="filteredCustomersForMobile"
                (completeMethod)="filterCustomersByMobile($event)"
                (onSelect)="onMobileSelect($event)"
                (input)="onMobileInput($event)"
                field="mobile_no"
                [dropdown]="false"
                [forceSelection]="false"
                [minLength]="0"
                [maxlength]="10"
                [showEmptyMessage]="true"
                emptyMessage="No customer found with this mobile"
                placeholder="Search or enter 10-digit mobile number"
                appendTo="body"
                styleClass="w-100"
                inputStyleClass="w-100 custom-form-input ps-4"
              >
                <ng-template let-customer pTemplate="item">
                  <div class="d-flex justify-content-between align-items-center py-1">
                    <div>
                      <div class="fw-bold text-dark" style="font-size: var(--fs-md);">
                        {{ customer.mobile_no }}
                      </div>
                      <small class="text-muted" style="font-size: var(--fs-sm);">
                        {{ customer.customer_name }}
                      </small>
                    </div>
                  </div>
                </ng-template>
              </p-autoComplete>
            </div>
            <small class="text-danger d-flex align-items-center gap-1 mt-1" *ngIf="mobileError">
              <i class="pi pi-exclamation-triangle"></i>{{ mobileError }}
            </small>
          </div>

          <!-- Valid Until -->
          <div class="form-group mb-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="field-label" for="qo-validity">
                Valid Until <span class="text-danger">*</span>
              </label>
              <span class="validity-badge">Default: 7 days</span>
            </div>
            <div class="calendar-wrapper">
              <p-calendar
                [(ngModel)]="form.validUntil"
                [showIcon]="true"
                [minDate]="minDate"
                [maxDate]="maxDate"
                dateFormat="dd/mm/yy"
                placeholder="Select validity date"
                appendTo="body"
                inputStyleClass="custom-form-input"
                styleClass="w-100 custom-calendar-control"
              ></p-calendar>
            </div>
          </div>

          <!-- Tax Option -->
          <div class="form-group mb-1">
            <label class="field-label mb-2">PDF Type</label>
            <div class="tax-toggle-container">
              <div
                class="tax-toggle-btn"
                (click)="form.withGst = true"
                (keydown.enter)="form.withGst = true"
                (keydown.space)="form.withGst = true; $event.preventDefault()"
                [class.active]="form.withGst"
                role="button"
                tabindex="0"
              >
                <i class="pi pi-file-pdf"></i>
                <div class="tax-toggle-text">
                  <span class="tax-toggle-title">With Tax</span>
                  <small class="tax-toggle-desc">CGST/SGST/IGST breakdown</small>
                </div>
                <i class="pi pi-check tax-check-icon" *ngIf="form.withGst"></i>
              </div>

              <div
                class="tax-toggle-btn"
                (click)="form.withGst = false"
                (keydown.enter)="form.withGst = false"
                (keydown.space)="form.withGst = false; $event.preventDefault()"
                [class.active]="!form.withGst"
                role="button"
                tabindex="0"
              >
                <i class="pi pi-file"></i>
                <div class="tax-toggle-text">
                  <span class="tax-toggle-title">Without Tax</span>
                  <small class="tax-toggle-desc">Base price only</small>
                </div>
                <i class="pi pi-check tax-check-icon" *ngIf="!form.withGst"></i>
              </div>
            </div>
          </div>

        </div>

        <div class="premium-footer">
          <button
            class="premium-btn premium-btn-secondary"
            (click)="onCancel()"
            type="button"
          >
            Cancel
          </button>
          <button
            class="premium-btn premium-btn-primary"
            (click)="onSave()"
            type="button"
            [disabled]="!isFormValid()"
          >
            <i class="pi pi-file-pdf"></i>
            Generate PDF
          </button>
        </div>
    </p-dialog>
  `,
  styles: [`
    /* Chrome (header, body padding, footer, buttons) comes from styles/components/_dialogs.scss. */
    .form-group {
      margin-bottom: var(--sp-4);
    }

    .field-label {
      font-size: var(--fs-base);
      font-weight: var(--fw-semibold);
      color: var(--fg-muted);
    }

    .optional-badge,
    .validity-badge {
      padding: 1px var(--sp-2);
      border-radius: var(--r-sm);
      font-size: var(--fs-xs);
    }

    .optional-badge {
      color: var(--fg-subtle);
      background: var(--bg-muted);
    }

    .validity-badge {
      font-weight: var(--fw-medium);
      color: var(--accent-fg);
      background: var(--accent-subtle);
    }

    /* Leading icon inside the autocomplete field */
    .input-with-icon-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    :host ::ng-deep .input-with-icon-wrapper p-autocomplete,
    :host ::ng-deep .input-with-icon-wrapper .p-autocomplete {
      display: flex;
      flex: 1 1 100%;
      width: 100%;
    }

    .input-lead-icon {
      position: absolute;
      left: var(--sp-3);
      z-index: 2;
      color: var(--fg-subtle);
      font-size: var(--fs-base);
      pointer-events: none;
    }

    :host ::ng-deep .custom-form-input.ps-4 {
      padding-left: var(--sp-8) !important;
    }

    :host ::ng-deep .custom-calendar-control {
      display: flex;
      width: 100%;

      .p-inputtext {
        flex: 1 1 auto;
        min-height: var(--control-h);
        font-size: var(--fs-md);
      }
    }

    /* With / without tax choice */
    .tax-toggle-container {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--sp-3);
    }

    .tax-toggle-btn {
      display: flex;
      align-items: center;
      gap: var(--sp-3);
      padding: var(--sp-3);
      border: 1.5px solid var(--line);
      border-radius: var(--r-lg);
      background: var(--bg-subtle);
      cursor: pointer;
      transition: border-color var(--dur-fast) var(--ease), background-color var(--dur-fast) var(--ease);

      i.pi-file-pdf, i.pi-file {
        flex-shrink: 0;
        font-size: var(--fs-xl);
        color: var(--fg-subtle);
      }

      &:hover {
        border-color: var(--accent-light);
        background: var(--accent-subtle);
      }

      &:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
      }

      &.active {
        border-color: var(--accent);
        background: var(--accent-subtle);

        i.pi-file-pdf, i.pi-file { color: var(--accent); }
      }
    }

    .tax-toggle-text {
      display: flex;
      flex: 1;
      flex-direction: column;
      min-width: 0;
    }

    .tax-toggle-title {
      font-size: var(--fs-base);
      font-weight: var(--fw-semibold);
      color: var(--fg);
    }

    .tax-toggle-desc {
      overflow: hidden;
      font-size: var(--fs-xs);
      color: var(--fg-muted);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .tax-check-icon {
      flex-shrink: 0;
      font-size: var(--fs-base);
      color: var(--accent);
    }
  `],
})
export class QuotationOptionsDialogComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Pre-filled customer name from the layout header or POS cart */
  @Input() prefillCustomerName: string = '';
  /** Pre-filled mobile from the layout header or POS cart */
  @Input() prefillMobileNumber: string = '';
  /** Default validity date */
  @Input() defaultValidityDate: Date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  @Output() saved = new EventEmitter<QuotationOptions>();
  @Output() cancelled = new EventEmitter<void>();

  form: QuotationOptions = {
    customerName: '',
    mobileNumber: '',
    customerAddress: '',
    customerGSTIN: '',
    validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    withGst: true,
  };

  mobileError: string = '';

  /** Cached customer list loaded from /order/getcustomer */
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  filteredCustomersForMobile: Customer[] = [];
  private isLoadingCustomers: boolean = false;

  readonly minDate: Date = new Date();
  readonly maxDate: Date = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  constructor(private offerGroupService: OfferGroupService) { }

  ngOnInit(): void {
    this.ensureCustomersLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      // Pre-fill from current layout/POS state
      this.form = {
        customerName: this.prefillCustomerName || '',
        mobileNumber: this.prefillMobileNumber || '',
        customerAddress: '',
        customerGSTIN: '',
        validUntil: this.defaultValidityDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        withGst: true,
      };
      this.mobileError = '';
      this.ensureCustomersLoaded();
      this.validateMobile();
    }
  }

  /**
   * Load customers on demand from API
   */
  async ensureCustomersLoaded(): Promise<void> {
    if (this.customers.length > 0 || this.isLoadingCustomers) return;

    this.isLoadingCustomers = true;
    try {
      const customerList = await this.offerGroupService.getCustomers().toPromise();
      this.customers = customerList || [];
    } catch (e) {
      console.warn('[QuotationOptionsDialog] Could not load customer list:', e);
    } finally {
      this.isLoadingCustomers = false;
    }
  }

  async filterCustomers(event: any): Promise<void> {
    await this.ensureCustomersLoaded();
    const query = (event.query || '').toLowerCase().trim();
    if (!query) {
      this.filteredCustomers = this.customers.slice(0, 20);
    } else {
      this.filteredCustomers = this.customers.filter(
        (c) =>
          (c.customer_name && c.customer_name.toLowerCase().includes(query)) ||
          (c.mobile_no && c.mobile_no.includes(query)),
      );
    }
  }

  async filterCustomersByMobile(event: any): Promise<void> {
    await this.ensureCustomersLoaded();
    const query = (event.query || '').toLowerCase().trim();
    if (!query) {
      this.filteredCustomersForMobile = this.customers.slice(0, 20);
    } else {
      this.filteredCustomersForMobile = this.customers.filter(
        (c) =>
          (c.mobile_no && c.mobile_no.includes(query)) ||
          (c.customer_name && c.customer_name.toLowerCase().includes(query)),
      );
    }
  }

  onCustomerSelect(event: any): void {
    const customer: Customer = event.value || event;
    if (customer && typeof customer === 'object') {
      this.form.customerName = customer.customer_name || '';
      this.form.mobileNumber = customer.mobile_no || '';
      this.form.customerGSTIN = customer.gst_no || '';
      this.form.customerAddress = [
        customer.address_line1,
        customer.address_line2,
        customer.city,
        customer.state,
        customer.zip_code,
      ]
        .filter(Boolean)
        .join(', ');
      this.validateMobile();
    }
  }

  onMobileSelect(event: any): void {
    const customer: Customer = event.value || event;
    if (customer && typeof customer === 'object') {
      this.form.mobileNumber = customer.mobile_no || '';
      this.form.customerName = customer.customer_name || '';
      this.form.customerGSTIN = customer.gst_no || '';
      this.form.customerAddress = [
        customer.address_line1,
        customer.address_line2,
        customer.city,
        customer.state,
        customer.zip_code,
      ]
        .filter(Boolean)
        .join(', ');
      this.validateMobile();
    }
  }

  onCustomerNameModelChange(value: any): void {
    if (typeof value === 'string') {
      this.form.customerName = value;
    } else if (value && typeof value === 'object' && 'customer_name' in value) {
      this.onCustomerSelect(value);
    }
  }

  onMobileNumberModelChange(value: any): void {
    if (typeof value === 'string') {
      const numeric = value.replace(/\D/g, '');
      this.form.mobileNumber = numeric;
      this.validateMobile();
    } else if (value && typeof value === 'object' && 'mobile_no' in value) {
      this.onMobileSelect(value);
    }
  }

  onMobileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input) {
      input.value = input.value.replace(/\D/g, '');
      this.form.mobileNumber = input.value;
      this.validateMobile();
    }
  }

  private validateMobile(): void {
    const mobile = this.form.mobileNumber;
    if (!mobile || mobile.trim() === '') {
      this.mobileError = '';
      return;
    }
    if (mobile.length !== 10) {
      this.mobileError = 'Mobile number must be exactly 10 digits';
    } else {
      this.mobileError = '';
    }
  }

  isFormValid(): boolean {
    if (!this.form.validUntil) return false;
    if (this.form.mobileNumber && this.form.mobileNumber.length !== 10) return false;
    return true;
  }

  onSave(): void {
    this.validateMobile();
    if (!this.isFormValid()) return;

    // Emit the exact values typed/selected in the dialog
    this.saved.emit({
      customerName: (this.form.customerName || '').trim(),
      mobileNumber: (this.form.mobileNumber || '').trim(),
      customerAddress: this.form.customerAddress || '',
      customerGSTIN: this.form.customerGSTIN || '',
      validUntil: this.form.validUntil,
      withGst: this.form.withGst,
    });
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onCancel(): void {
    this.cancelled.emit();
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
  }
}
